import { createContext, useContext, useEffect, useRef } from "react";

/**
 * Живые обновления страницы: подписка на события сервера (SSE, /api/live)
 * плюс опрос раз в pollMs — на случай, если события не доходят (прокси,
 * несколько процессов). Вкладка в фоне не опрашивает; вернулись — перечитали.
 *
 * Поток событий один на вкладку и общий для всех подписчиков: разделы не
 * размонтируются при переключении (App.tsx), и каждый со своим EventSource
 * держал бы по соединению — при лимите браузера в шесть на адрес две вкладки
 * оставляли бы обычным запросам одно-два. Открывается с первым подписчиком,
 * закрывается с последним.
 *
 * onChange вызывается не чаще раза в полсекунды: несколько подряд правок
 * коллег — одна перечитка. Пока `paused` (открыт диалог, тянут строку),
 * событие откладывается и срабатывает после. Скрытый раздел (см.
 * SectionActive) тоже не перечитывает: показали — перечитал один раз.
 */

/** Раздел показан на экране; провайдер — App, вокруг каждого раздела. */
export const SectionActive = createContext(true);

type Listener = (topic: string) => void;
const listeners = new Set<Listener>();
let stream: EventSource | null = null;
let closeTimer: number | null = null;

function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  if (closeTimer) {
    window.clearTimeout(closeTimer);
    closeTimer = null;
  }
  if (!stream) {
    try {
      stream = new EventSource("/api/live");
      stream.addEventListener("change", (ev) => {
        const topic = String((ev as MessageEvent).data);
        for (const l of listeners) l(topic);
      });
    } catch {
      stream = null;
    }
  }
  return () => {
    listeners.delete(fn);
    // Закрывается не сразу: раздел, который перемонтировали (смена key,
    // StrictMode), подписывается снова через мгновение — без переподключения.
    if (!listeners.size && stream && !closeTimer) {
      closeTimer = window.setTimeout(() => {
        closeTimer = null;
        if (!listeners.size && stream) {
          stream.close();
          stream = null;
        }
      }, 1000);
    }
  };
}

export function useLive(
  topics: string[],
  onChange: () => void,
  {
    pollMs = 30_000,
    paused = false,
  }: { pollMs?: number; paused?: boolean } = {},
) {
  const active = useContext(SectionActive);
  const cb = useRef(onChange);
  cb.current = onChange;
  const held = !active || paused;
  const heldRef = useRef(held);
  const pending = useRef(false);
  useEffect(() => {
    heldRef.current = held;
    if (!held && pending.current) {
      pending.current = false;
      cb.current();
    }
  }, [held]);

  useEffect(() => {
    let timer: number | null = null;
    const fire = () => {
      if (document.visibilityState !== "visible") return;
      if (heldRef.current) {
        pending.current = true;
        return;
      }
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => cb.current(), 500);
    };
    const wanted = new Set(topics);
    const unsubscribe = subscribe((topic) => {
      if (wanted.has(topic)) fire();
    });
    const poll = window.setInterval(fire, pollMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") fire();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsubscribe();
      window.clearInterval(poll);
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics.join(","), pollMs]);
}
